// SPDX-License-Identifier: AGPL-3.0-or-later

import {msg} from '@lingui/core/macro';

/** Shared English source strings for guild boost UI (extracted into locale catalogs). */

export const GUILD_BOOST_MENU_LABEL_DESCRIPTOR = msg({
	message: 'Server Boost',
	context: 'guild-boost',
	comment: 'Menu item and community settings tab label for the guild boost feature.',
});

export const GUILD_BOOST_CONFIRM_TITLE_DESCRIPTOR = msg({
	message: 'Boost this community?',
	context: 'guild-boost',
	comment: 'Title on the confirmation modal before applying a guild boost.',
});

export const GUILD_BOOST_CONFIRM_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Are you sure you want to boost this community?',
	context: 'guild-boost',
	comment: 'Confirmation question before applying a guild boost to the community.',
});

export const GUILD_BOOST_CONFIRM_PRIMARY_DESCRIPTOR = msg({
	message: 'Boost',
	context: 'guild-boost',
	comment: 'Primary confirm button to apply a guild boost.',
});

export const GUILD_BOOST_APPLY_FAILED_DESCRIPTOR = msg({
	message: "Couldn't apply server boost",
	context: 'guild-boost',
	comment: 'Error modal title when applying a guild boost fails.',
});

export const GUILD_BOOST_NO_SLOTS_DESCRIPTOR = msg({
	message: 'You have no boost slots left to use on this community.',
	context: 'guild-boost',
	comment: 'Shown in boost confirm modal when premium user has no available slots.',
});

export const GUILD_BOOST_PREMIUM_REQUIRED_DESCRIPTOR = msg({
	message: 'Premium membership is required to boost communities.',
	context: 'guild-boost',
	comment: 'Shown when the user needs Plutonium/premium to boost a community.',
});

export const GUILD_BOOST_LOADING_STATUS_DESCRIPTOR = msg({
	message: 'Loading boost status…',
	context: 'guild-boost',
	comment: 'Loading state while boost slot availability is fetched.',
});

export const GUILD_BOOST_SLOTS_AVAILABLE_DESCRIPTOR = msg({
	message:
		'{available} of {total} boost slots available (premium members get {perPremium} slots).',
	context: 'guild-boost',
	comment:
		'Boost slot availability summary. available/total are counts; perPremium is slots granted to premium members.',
});

export const GUILD_BOOST_THANK_YOU_TITLE_DESCRIPTOR = msg({
	message: 'Thank you for boosting!',
	context: 'guild-boost',
	comment: 'Title on the celebration modal after a member boosts a community.',
});

export const GUILD_BOOST_THANK_YOU_BODY_DESCRIPTOR = msg({
	message: 'Your boost helps unlock perks for {guildName} and everyone in it.',
	context: 'guild-boost',
	comment: 'Body on the celebration modal after boosting. guildName is the community name.',
});

export const GUILD_BOOST_LOAD_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't load server boost status",
	context: 'guild-boost',
	comment: 'Error modal title when guild boost settings fail to load.',
});

export const GUILD_BOOST_REMOVE_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't remove server boost",
	context: 'guild-boost',
	comment: 'Error modal title when removing a guild boost fails.',
});

export const GUILD_BOOST_LOAD_FAILED_INLINE_DESCRIPTOR = msg({
	message: 'Could not load server boost information.',
	context: 'guild-boost',
	comment: 'Inline error when the boost settings page fails to load.',
});

export const GUILD_BOOST_NO_BOOSTERS_DESCRIPTOR = msg({
	message: 'No one is boosting this community yet. Be the first!',
	context: 'guild-boost',
	comment: 'Empty state in the server boost settings booster list.',
});

export const GUILD_BOOST_BOOSTERS_SECTION_DESCRIPTOR = msg({
	message: 'Boosters',
	context: 'guild-boost',
	comment: 'Section heading for members currently boosting the community.',
});

export const GUILD_BOOST_YOUR_SLOTS_SECTION_DESCRIPTOR = msg({
	message: 'Your boost slots',
	context: 'guild-boost',
	comment: 'Section heading for the current user guild boost slot availability.',
});

export const GUILD_BOOST_LEVELS_SECTION_DESCRIPTOR = msg({
	message: 'Boost levels',
	context: 'guild-boost',
	comment: 'Section heading for the three guild boost perk tiers.',
});

export const GUILD_BOOST_MAX_LEVEL_DESCRIPTOR = msg({
	message: 'Maximum level reached',
	context: 'guild-boost',
	comment: 'Progress label when the community is at the highest boost level.',
});

export const GUILD_BOOST_LEVEL_BADGE_DESCRIPTOR = msg({
	message: 'Level {level}',
	context: 'guild-boost',
	comment: 'Badge or card title for a boost level number.',
});

export const GUILD_BOOST_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# boost} other {# boosts}}',
	context: 'guild-boost',
	comment: 'Boost count label. count is the number of boosts.',
});

export const GUILD_BOOST_GOAL_PILL_DESCRIPTOR = msg({
	message: 'Boost goal',
	context: 'guild-boost',
	comment: 'Compact pill label on the guild sidebar boost progress bar when the community is below level 1.',
});

export const GUILD_BOOST_PROGRESS_FRACTION_DESCRIPTOR = msg({
	message: '{current}/{required} boosts',
	context: 'guild-boost',
	comment:
		'Compact sidebar boost progress fraction. current is applied boosts; required is the next level threshold.',
});

export const GUILD_BOOST_PROGRESS_TO_LEVEL_DESCRIPTOR = msg({
	message: '{current} / {required} boosts to level {nextLevel}',
	context: 'guild-boost',
	comment:
		'Progress toward the next boost level. current/required are counts; nextLevel is the upcoming level number.',
});

export const GUILD_BOOST_SLOTS_SHORT_DESCRIPTOR = msg({
	message: '{available} of {total} slots available',
	context: 'guild-boost',
	comment: 'Short slot availability line on the boost settings page.',
});

export const GUILD_BOOST_PREMIUM_SLOTS_USAGE_DESCRIPTOR = msg({
	message:
		'Premium members receive {perPremium} boost slots. You have used {usedOnGuild} on this community.',
	context: 'guild-boost',
	comment:
		'Explains premium slot grant and usage on this community. perPremium and usedOnGuild are counts.',
});

export const GUILD_BOOST_APPLY_CTA_DESCRIPTOR = msg({
	message: 'Boost this community',
	context: 'guild-boost',
	comment: 'Primary button on the boost settings page to open the boost confirm flow.',
});

export const GUILD_BOOST_PROGRESS_BAR_TOGGLE_SECTION_DESCRIPTOR = msg({
	message: 'Boost progress bar',
	context: 'guild-boost',
	comment: 'Section heading for the guild sidebar boost progress bar visibility toggle.',
});

export const GUILD_BOOST_PROGRESS_BAR_TOGGLE_LABEL_DESCRIPTOR = msg({
	message: 'Display boost progress bar',
	context: 'guild-boost',
	comment: 'Switch label that shows or hides the boost progress bar under the community banner or name.',
});

export const GUILD_BOOST_PROGRESS_BAR_TOGGLE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Shows a boost goal progress bar below the community banner or name in the channel list.',
	context: 'guild-boost',
	comment: 'Switch description for the sidebar boost progress bar visibility setting.',
});

export const GUILD_BOOST_REMOVE_CTA_DESCRIPTOR = msg({
	message: 'Remove my boost',
	context: 'guild-boost',
	comment: 'Secondary button to remove the current user boost from this community.',
});

export const GUILD_BOOST_MEMBER_LIST_TOOLTIP_DESCRIPTOR = msg({
	message: 'Since {date}\nServer Boosting',
	context: 'guild-boost',
	comment:
		'Member list boost icon tooltip. First line is the date and “since”; second line continues after a line break. {date} looks like 28.09.2026.',
});
